import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Image, Phone, Mail, Heart, Send, Share2, Users, TrendingUp, Bed, Bath, Car, MapPin, Zap, ArrowUpDown, Search, Calendar, Flame, Calculator, FileSignature } from 'lucide-react';

const STATUS_TABS = [
    { value: 'current', label: 'Current' },
    { value: 'sold', label: 'Sold' },
    { value: 'withdrawn', label: 'Withdrawn' },
];

// Visual styling for each Property Demand Score tier (returned by the backend).
const DEMAND_TIERS = {
    red_hot: { label: 'Red Hot', text: 'text-rose-700', bg: 'bg-rose-50', bar: 'bg-rose-500', ring: 'border-rose-200' },
    hot: { label: 'Hot', text: 'text-orange-700', bg: 'bg-orange-50', bar: 'bg-orange-500', ring: 'border-orange-200' },
    warm: { label: 'Warm', text: 'text-amber-700', bg: 'bg-amber-50', bar: 'bg-amber-500', ring: 'border-amber-200' },
    cold: { label: 'Cold', text: 'text-slate-600', bg: 'bg-slate-50', bar: 'bg-slate-400', ring: 'border-slate-200' },
};
const getDemandTier = (tier) => DEMAND_TIERS[tier] || DEMAND_TIERS.cold;

const formatPriceValue = (price) => {
    if (!price) return null;
    const num = Number(price);
    if (isNaN(num)) return price;
    return `$${Math.round(num).toLocaleString()}`;
};

const getPriceTypeLabel = (priceDisplay) => {
    const labels = {
        exact: 'Asking Price',
        range: 'Price Range',
        contact: 'Contact Agent',
        auction: 'Guided Price',
    };
    return labels[priceDisplay] || 'Asking Price';
};

// Mirrors PropertyCard so analytics cards show the same price as the listings tab.
const getPriceDisplay = (details) => {
    if (!details) return 'Contact Agent';

    const freeform = (details.price_display_text || '').trim();
    if (freeform) return freeform;

    const priceDisplay = details.price_display || 'exact';
    const priceTypeLabel = getPriceTypeLabel(priceDisplay);
    const priceValue = details.price;

    if (priceDisplay === 'range') {
        const priceTo = details.price_to;
        if (priceValue && priceTo && Number(priceTo) !== Number(priceValue)) {
            return `${formatPriceValue(priceValue)} – ${formatPriceValue(priceTo)}`;
        }
        if (priceValue) return formatPriceValue(priceValue);
        return '';
    }

    if (priceDisplay === 'contact') return priceTypeLabel;

    if (priceDisplay === 'auction') {
        if (priceValue) return `${priceTypeLabel}: ${formatPriceValue(priceValue)}`;
        return priceTypeLabel;
    }

    if (priceDisplay === 'exact') {
        if (priceValue) return formatPriceValue(priceValue);
        return 'Contact Agent';
    }

    if (!priceValue) return `${priceTypeLabel}: Contact Agent`;
    return `${priceTypeLabel}: ${formatPriceValue(priceValue)}`;
};

const StatusToggle = ({ statusFilter, onStatusChange }) => {
    if (!onStatusChange) return null;
    return (
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {STATUS_TABS.map((tab) => (
                <button
                    key={tab.value}
                    type="button"
                    onClick={() => onStatusChange(tab.value)}
                    className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
                        statusFilter === tab.value
                            ? 'bg-[#55C5D0] text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );
};

const PropertyInteractionStats = ({
    properties,
    loading,
    refreshing = false,
    sortBy,
    onSortChange,
    statusFilter = 'current',
    onStatusChange,
    searchQuery = '',
    onSearchChange,
}) => {
    const navigate = useNavigate();

    const emptyLabel = searchQuery.trim()
        ? 'No properties match your search'
        : ({
            current: 'No interactions found for current listings',
            sold: 'No interactions found for sold properties',
            withdrawn: 'No interactions found for withdrawn properties',
        }[statusFilter] || 'No property interactions found');

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900">Top Performing Properties</h2>
                    <StatusToggle statusFilter={statusFilter} onStatusChange={onStatusChange} />
                </div>
                <div className="flex items-center justify-center h-40">
                    <div className="text-gray-400">Loading property interactions...</div>
                </div>
            </div>
        );
    }

    const handlePropertyClick = (propertyId) => {
        navigate(`/properties/${propertyId}?tab=analytics`);
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const sortOptions = [
        { value: 'demand_score', label: 'Demand Score', icon: Flame },
        { value: 'total_interactions', label: 'Total Interactions', icon: TrendingUp },
        { value: 'gallery_views', label: 'Gallery Views', icon: Image },
        { value: 'intent_clicks', label: 'Intent Clicks (Phone/Email)', icon: Zap },
        { value: 'enquiries_sent', label: 'Enquiries Sent', icon: Send },
        { value: 'shares_clicked', label: 'Shares', icon: Share2 },
        { value: 'property_views', label: 'Property Views', icon: Eye },
        { value: 'newest', label: 'Newest to Oldest', icon: Calendar },
        { value: 'oldest', label: 'Oldest to Newest', icon: Calendar },
    ];

    const showRankBadges = sortBy === 'total_interactions' && !searchQuery.trim();
    const hasProperties = properties && properties.length > 0;

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <TrendingUp className="w-7 h-7 text-gray-400" />
                            Top Performing Properties
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Properties ranked by engagement metrics - click to view details
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <StatusToggle statusFilter={statusFilter} onStatusChange={onStatusChange} />
                        <ArrowUpDown className="w-5 h-5 text-gray-500" />
                        <select
                            value={sortBy}
                            onChange={(e) => onSortChange(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        >
                            {sortOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    Sort by: {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {onSearchChange && (
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="search"
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search by address, suburb, or title…"
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-[#55C5D0]"
                        />
                    </div>
                )}
            </div>

            {!hasProperties ? (
                <div className="flex items-center justify-center h-40">
                    <div className="text-gray-400">{emptyLabel}</div>
                </div>
            ) : (
            <>
            {/* While switching tabs/sort with existing data on screen, dim the grid and
                overlay a spinner so the change registers immediately instead of the old
                list appearing frozen until the new data arrives. */}
            <div className="relative">
                {refreshing && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 rounded-lg">
                        <div className="flex items-center gap-2 text-gray-600 text-sm font-medium bg-white px-4 py-2 rounded-full shadow">
                            <svg className="animate-spin h-4 w-4 text-[#55C5D0]" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                            Updating…
                        </div>
                    </div>
                )}
                <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 transition-opacity ${refreshing ? 'opacity-50' : 'opacity-100'}`}>
                {properties.map((property, index) => {
                    const details = property.property_details || {};
                    const primaryImage = details.primary_image || 'https://via.placeholder.com/400x300?text=No+Image';

                    // Match the properties-tab PropertyCard car-space computation:
                    // sum parkings + car_ports + open_spaces + parking_spaces, falling
                    // back to `parking` when none of the structured fields are populated.
                    const totalCarSpaces = (
                        (Number(details.parkings) || 0)
                        + (Number(details.car_ports) || 0)
                        + (Number(details.open_spaces) || 0)
                        + (Number(details.parking_spaces) || 0)
                    ) || (Number(details.parking) || 0);

                    return (
                        <div
                            key={property.property_id}
                            onClick={() => handlePropertyClick(property.property_id)}
                            className="bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer hover:shadow-xl hover:border-[#55C5D0] transition-all duration-300 transform hover:-translate-y-1 relative group"
                        >
                            {/* Rank Badge — only when sorted by total interactions */}
                            {showRankBadges && index < 3 && (
                                <div className="absolute top-3 left-3 z-10">
                                    <div className={`
                                        w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-base shadow-lg
                                        ${index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' :
                                                'bg-gradient-to-br from-orange-400 to-orange-600'}
                                    `}>
                                        #{index + 1}
                                    </div>
                                </div>
                            )}

                            {/* Property Image */}
                            <div className="relative h-48 overflow-hidden bg-gray-200">
                                <img
                                    src={primaryImage}
                                    alt={details.address || `Property ${property.property_id}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                    onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                                    }}
                                />

                                {/* Method of Sale Badge */}
                                {details.method_of_sale && (
                                    <div className="absolute top-3 right-3 bg-[#55C5D0] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                                        {details.method_of_sale}
                                    </div>
                                )}
                            </div>

                            {/* Property Details */}
                            <div className="p-4">
                                {/* Demand Score — composite 0-100 "are buyers chasing this?" */}
                                {(() => {
                                    const tier = getDemandTier(property.demand_tier);
                                    const score = property.demand_score ?? 0;
                                    return (
                                        <div className={`mb-3 rounded-lg border ${tier.ring} ${tier.bg} px-3 py-2`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-1.5">
                                                    <Flame className={`w-4 h-4 ${tier.text}`} />
                                                    <span className={`text-xs font-semibold uppercase tracking-wide ${tier.text}`}>
                                                        {tier.label} demand
                                                    </span>
                                                </div>
                                                <span className={`text-lg font-bold ${tier.text}`}>{score}<span className="text-xs font-medium opacity-70">/100</span></span>
                                            </div>
                                            <div className="mt-1.5 h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
                                                <div className={`h-full rounded-full ${tier.bar}`} style={{ width: `${Math.max(2, Math.min(100, score))}%` }} />
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Price — matches listings tab display */}
                                <div className="mb-2">
                                    <span className="text-xl font-bold text-gray-900">
                                        {getPriceDisplay(details)}
                                    </span>
                                </div>

                                {/* Address */}
                                <div className="mb-3">
                                    <h3 className="text-base font-bold text-gray-900 line-clamp-2">
                                        {details.address || details.title || `Property #${property.property_id}`}
                                    </h3>
                                    {details.suburb && (
                                        <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                                            <MapPin className="w-4 h-4" />
                                            <span className="line-clamp-1">
                                                {details.suburb}{details.state ? `, ${details.state}` : ''}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Property Features */}
                                {(details.beds || details.baths || totalCarSpaces) && (
                                    <div className="flex items-center gap-4 mb-3 text-sm text-gray-700">
                                        {details.beds && (
                                            <div className="flex items-center gap-1">
                                                <Bed className="w-4 h-4" />
                                                <span>{details.beds}</span>
                                            </div>
                                        )}
                                        {details.baths && (
                                            <div className="flex items-center gap-1">
                                                <Bath className="w-4 h-4" />
                                                <span>{details.baths}</span>
                                            </div>
                                        )}
                                        {totalCarSpaces > 0 && (
                                            <div className="flex items-center gap-1">
                                                <Car className="w-4 h-4" />
                                                <span>{totalCarSpaces}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Analytics Metrics */}
                                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div className="flex items-center gap-1.5">
                                            <Eye className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">Views:</span>
                                            <span className="font-semibold text-gray-900">{property.property_views}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Image className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">Gallery:</span>
                                            <span className="font-semibold text-gray-900">{property.gallery_views}</span>
                                        </div>
                                        {/* "Calls" and "Emails" are things a BUYER did on the
                                            portal - tapped the agent's phone number, tapped the
                                            agent's email address. They are not emails we sent.
                                            Christian read "Emails: 0" on a listing as proof the
                                            matched-buyer alert had not gone out (Monday
                                            13058812936); the alert had genuinely not gone out, but
                                            this number would have said 0 either way. Named for
                                            what they actually are so the two can't be confused
                                            again - sends live on Communications Hub > Sent. */}
                                        <div className="flex items-center gap-1.5">
                                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600" title="Buyers who tapped the agent's phone number on this listing">Agent called:</span>
                                            <span className="font-semibold text-gray-900">{property.calls_clicked}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600" title="Buyers who tapped the agent's email address on this listing. This is not the number of emails we sent - see Communications Hub > Sent for that.">Agent emailed:</span>
                                            <span className="font-semibold text-gray-900">{property.emails_clicked}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Send className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">Enquiries:</span>
                                            <span className="font-semibold text-gray-900">{property.enquiries_sent}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Heart className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">Saved:</span>
                                            <span className="font-semibold text-gray-900">{property.properties_saved}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Share2 className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">Shares:</span>
                                            <span className="font-semibold text-gray-900">{property.shares_clicked || 0}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Calculator className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">Calc:</span>
                                            <span className="font-semibold text-gray-900">{property.calc_runs || 0}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <FileSignature className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">Offers:</span>
                                            <span className="font-semibold text-gray-900">{property.offers_submitted || 0}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Total Interactions & Unique Buyers */}
                                <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
                                    <div className="flex items-center gap-1.5 text-[#55C5D0] font-semibold">
                                        <TrendingUp className="w-4 h-4 text-gray-400" />
                                        <span>{property.total_interactions} interactions</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-gray-600">
                                        <Users className="w-4 h-4" />
                                        <span>{property.unique_buyers} buyers</span>
                                    </div>
                                </div>

                                {/* Last Interaction */}
                                <div className="text-xs text-gray-500 mt-2 text-center">
                                    Last activity: {formatTimestamp(property.last_interaction)}
                                </div>
                            </div>
                        </div>
                    );
                })}
                </div>
            </div>

            {properties.length >= 50 && (
                <div className="mt-6 text-center text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                    Showing top 50 properties. Adjust date range or sort order to see different results.
                </div>
            )}
            </>
            )}
        </div>
    );
};

export default PropertyInteractionStats;
